import { forwardRef, useImperativeHandle, useState } from "react";
import { Button } from "../../components/BaseComponents";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import ResendInviteModal from "../../components/BaseComponents/ResendInviteModal/ResendInviteModal";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import useApi from "../../hooks/useApi";
import usePaginatedFetch from "../../hooks/usePaginatedFetch";
import { formatDate, getCSRFToken } from "../../lib/utils";
import { PaginationMeta } from "../../types/PaginationMeta";
import AddTrainingUserModal from "./AddTrainingUserModal";
import EditTrainingUserModal from "./EditTrainingUserModal";
import ManageTrainingOrganisationsModal from "./ManageTrainingOrganisationsModal";
import UploadTrainingUsersModal from "./UploadTrainingUsersModal";

// Picks up a trainee's pending -> active change without a manual reload; no SSE exists in the app.
const POLL_INTERVAL_MS = 15000;

type TrainingSessionUsersResponse = {
    users: any[];
    meta: PaginationMeta;
};

type AdminTrainingSessionUsersProps = {
    trainingSessionId: string;
    sessionData?: any;
    userNameFilter?: string;
    organisationNameFilter?: string;
    onDataRefresh?: () => void;
};

function AdminTrainingSessionUsers({ 
    trainingSessionId,
    sessionData,
    userNameFilter = '',
    organisationNameFilter = '',
    onDataRefresh,
}: AdminTrainingSessionUsersProps, ref: any) {
    const api = useApi();
    const { rows: results, loading, pagination, onPageChange, refetch: onSearch } = usePaginatedFetch<TrainingSessionUsersResponse, any>({
        path: `/admin/training-sessions/${trainingSessionId}/users`,
        query: { userName: userNameFilter, organisationName: organisationNameFilter },
        getRows: (response) => response.users,
        pollIntervalMs: POLL_INTERVAL_MS,
    });

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editCandidate, setEditCandidate] = useState<any>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [manageOrganisationsModalOpen, setManageOrganisationsModalOpen] = useState(false);
    const [resendCandidate, setResendCandidate] = useState<any>(null);
    const [removeCandidate, setRemoveCandidate] = useState<any>(null);

    // Expose modal functions to parent via ref
    useImperativeHandle(ref, () => ({
        openAddModal: () => setAddModalOpen(true),
        openUploadModal: () => setUploadModalOpen(true),
        openManageOrganisationsModal: () => setManageOrganisationsModalOpen(true),
    }), []);



    const onResend = async () => {
        if (!resendCandidate) {
            return;
        }
        await api.post(`/admin/training-sessions/${trainingSessionId}/users/${resendCandidate.id}/resend-invite`, {
            _csrf: await getCSRFToken(),
        })
        setResendCandidate(null);
        onSearch();
    }

    const onRemove = async () => {
        if (!removeCandidate) {
            return;
        }
        await api.delete(`/admin/training-sessions/${trainingSessionId}/users/${removeCandidate.id}`, {
            _csrf: await getCSRFToken(),
        })
        setRemoveCandidate(null);
        onSearch();
    }

    const dateRenderer = (date: string | null) => {
        if (!date) {
            return <span>-</span>;
        }
        return <span title={date}>{ formatDate(date) }</span>
    }

    const columns: TableColumn[] = [{
        id: 'fullName',
        label: 'Name',
        renderer: (key, row) => <span>{ row[key] }{ row.type === 'invitation' ? <span className="text-charcoal-300"> (pending)</span> : '' }</span>,
    }, {
        id: 'email',
        label: 'Email',
    }, {
        id: 'organisationName',
        label: 'Organisation',
        // Sub-task I-extra2 §4.3/§8.2/§16.3: jurisdiction appended to the org name, e.g.
        // "Acme Training (DE)", matching the same treatment on the "Manage organisations" list.
        renderer: (key, row) => <span>{row[key]}{row.jurisdictionCode ? <span className="text-charcoal-300"> ({row.jurisdictionCode})</span> : ''}</span>,
    }, {
        id: 'dateAdded',
        label: 'Date Added',
        renderer: (key, row) => dateRenderer(row[key]),
    }, {
        id: 'inviteSentAt',
        label: 'Invite Sent',
        renderer: (key, row) => dateRenderer(row[key]),
    }, {
        id: 'requestedUpgrade',
        label: 'Requested Upgrade',
        renderer: (key, row) => <span>{ row[key] ? 'Requested' : '' }</span>,
    }, {
        id: 'actions',
        label: 'Actions',
        headerAlign: 'center',
        textAlign: 'right',
        renderer: (key, row) => (
            <div className="space-x-2">
                {row.type === 'invitation' ? (
                    <Button
                        type="Outlined"
                        size="sm"
                        icon="fa-solid fa-paper-plane"
                        text="Resend invite"
                        hasLeadingIcon
                        isTableRowButton
                        onClick={() => setResendCandidate(row)}
                    />
                ) : null}
                <Button
                    type="Primary"
                    icon="fa-solid fa-pen-to-square"
                    hasLeadingIcon
                    size="sm"
                    isTableRowButton
                    onClick={() => setEditCandidate(row)}
                    accessibilityLabel={`Edit ${row.fullName}`}
                />
                <Button
                    type="Primary"
                    icon="fa-solid fa-trash"
                    hasLeadingIcon
                    size="sm"
                    isTableRowButton
                    onClick={() => setRemoveCandidate(row)}
                    accessibilityLabel={`Delete ${row.fullName}`}
                />
            </div>
        ),
    }]

    return (
        <div>
            <SimpleSortTable
                columns={columns}
                data={results}
                isLoading={loading}
            />
            <Pagination
                onPageChange={onPageChange}
                pagination={pagination}
                isLoading={loading}
            />
            <AddTrainingUserModal
                isOpen={addModalOpen}
                trainingSessionId={trainingSessionId}
                sessionData={sessionData}
                onClose={() => setAddModalOpen(false)}
                onAdded={() => {
                    setAddModalOpen(false);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <EditTrainingUserModal
                isOpen={!!editCandidate}
                trainingSessionId={trainingSessionId}
                sessionData={sessionData}
                user={editCandidate}
                onClose={() => setEditCandidate(null)}
                onUpdated={() => {
                    setEditCandidate(null);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <UploadTrainingUsersModal
                isOpen={uploadModalOpen}
                trainingSessionId={trainingSessionId}
                onClose={() => setUploadModalOpen(false)}
                onUploaded={() => {
                    setUploadModalOpen(false);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <ManageTrainingOrganisationsModal
                isOpen={manageOrganisationsModalOpen}
                trainingSessionId={trainingSessionId}
                onClose={() => {
                    setManageOrganisationsModalOpen(false);
                    onSearch();
                    onDataRefresh?.();
                }}
            />
            <ResendInviteModal
                open={!!resendCandidate}
                onCancel={() => setResendCandidate(null)}
                onConfirm={onResend}
                fullName={resendCandidate?.fullName}
                email={resendCandidate?.email}
                signupUrl={resendCandidate?.signupUrl}
            />
            <ConfirmationModal
                open={!!removeCandidate}
                onCancel={() => setRemoveCandidate(null)}
                onConfirm={onRemove}
                requireTypedConfirmation="delete"
            >
                { removeCandidate && (
                    <div>
                        <PageHeadings title="Remove user from session?" />
                        <p className="text-white mt-4">
                            This will immediately and irreversibly remove {removeCandidate.fullName} ({removeCandidate.email})
                            from this training session, along with their training organisation and any sample data.
                            There is no undo.
                        </p>
                    </div>
                )}
            </ConfirmationModal>
        </div>
    )
}

export default forwardRef(AdminTrainingSessionUsers);
